import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, Zap, TrendingUp, LineChart } from "lucide-react";

interface AuditLoadingBrainProps {
  isOpen: boolean;
  progress: number;
  currentTask: string;
}

const floatingParticles = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  delay: i * 0.15,
  duration: 2 + Math.random() * 2,
}));

export const AuditLoadingBrain = ({ isOpen, progress, currentTask }: AuditLoadingBrainProps) => {
  const [pulseCount, setPulseCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      const interval = setInterval(() => {
        setPulseCount((prev) => prev + 1);
      }, 800);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full max-w-lg p-8"
          >
            {/* Neural Network Background */}
            <div className="absolute inset-0 overflow-hidden rounded-2xl">
              {floatingParticles.map((particle) => (
                <motion.div
                  key={particle.id}
                  className="absolute w-1 h-1 rounded-full bg-primary/30"
                  initial={{
                    x: Math.random() * 100 + "%",
                    y: Math.random() * 100 + "%",
                  }}
                  animate={{
                    x: [
                      Math.random() * 100 + "%",
                      Math.random() * 100 + "%",
                      Math.random() * 100 + "%",
                    ],
                    y: [
                      Math.random() * 100 + "%",
                      Math.random() * 100 + "%",
                      Math.random() * 100 + "%",
                    ],
                    scale: [1, 1.5, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: particle.duration,
                    repeat: Infinity,
                    delay: particle.delay,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </div>

            {/* Main Content */}
            <div className="relative bg-card/80 backdrop-blur-xl rounded-2xl p-8 border border-border/50 shadow-2xl">
              {/* 3D Brain Animation */}
              <div className="relative h-32 mb-6 flex items-center justify-center">
                <motion.div
                  className="relative"
                  animate={{
                    rotateY: [0, 360],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  style={{ transformStyle: "preserve-3d" }}
                >
                  <Brain className="w-24 h-24 text-primary" />
                  
                  {/* Orbiting Icons */}
                  {[Zap, TrendingUp, LineChart].map((Icon, i) => (
                    <motion.div
                      key={i}
                      className="absolute top-1/2 left-1/2"
                      animate={{
                        rotate: [i * 120, i * 120 + 360],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      style={{
                        transformOrigin: "0 0",
                      }}
                    >
                      <motion.div
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.6, 1, 0.6],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.3,
                        }}
                        style={{
                          transform: `translate(-50%, -50%) translateX(60px)`,
                        }}
                      >
                        <Icon className="w-6 h-6 text-primary" />
                      </motion.div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Pulse Rings */}
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={`pulse-${i}-${pulseCount}`}
                    className="absolute inset-0 rounded-full border-2 border-primary/30"
                    initial={{ scale: 0.5, opacity: 0.8 }}
                    animate={{ scale: 2.5, opacity: 0 }}
                    transition={{
                      duration: 2,
                      delay: i * 0.3,
                      ease: "easeOut",
                    }}
                  />
                ))}
              </div>

              {/* Progress Info */}
              <div className="space-y-4">
                <div className="text-center">
                  <motion.h3
                    className="text-xl font-semibold text-foreground mb-2"
                    animate={{ opacity: [1, 0.7, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    Analyzing Your CRM Data
                  </motion.h3>
                  <p className="text-sm text-muted-foreground">{currentTask}</p>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-mono text-primary font-semibold">{progress}%</span>
                  </div>
                  <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary via-primary/80 to-primary rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                      animate={{
                        x: ["-100%", "200%"],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      style={{ width: "50%" }}
                    />
                  </div>
                </div>

                {/* Fun Facts */}
                <motion.div
                  className="text-center text-xs text-muted-foreground pt-2"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  💡 AI is processing {Math.floor((progress / 100) * 47)} data points...
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
